import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import type { ReactNode } from "react";
import React from "react";
import { Element, scroller } from "react-scroll";
import { ChatMessageRow } from "~/components/common/chat/messages/ChatMessageRow";
import { useDebounce } from "~/hooks/useDebounce";
import type { Deal } from "~/models/Deal";
import type { Message, MessageSpeaker } from "~/models/Message";
import { postRead as postReadForClient } from "~/utils/api/deals/Message";
import { postRead as postReadForVendor } from "~/utils/api/vendors/deals/Message";
import Styles from "./ChatMessageArea.module.scss";

type Props = DeepReadonly<{
	deal: Deal;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
	messages: Message[];
	speaker: MessageSpeaker;
}>;

export const ChatMessageArea: React.FC<Props> = ({
	isOpen,
	deal,
	messages,
	speaker,
	handleMutate
}: Props) => {
	const debounce = useDebounce(2000);
	React.useEffect(() => {
		const f = async (): Promise<void> => {
			debounce(async () => {
				if (isOpen) {
					const readMethod =
						speaker === "maintenance_company"
							? postReadForClient
							: postReadForVendor;
					if (
						deal.messageSummaryUnread?.unread ||
						deal.messageSummaryUnread?.displayUnread
					) {
						await readMethod(deal.id);
					}
					await handleMutate();
				}
			});
			await handleMutate();
			scroller.scrollTo("chat-message-field-bottom", {
				containerId: "chat_messages_field",
				duration: 0,
				ignoreCancelEvents: true
			});
		};
		f().then();
	}, [isOpen, handleMutate, deal, speaker, debounce]);
	const renderRow = React.useCallback(
		(message: DeepReadonly<Message>): ReactNode => (
			<ChatMessageRow
				deal={deal}
				key={message.id}
				message={message}
				speaker={speaker}
			/>
		),
		[speaker, deal]
	);
	return (
		<div className={Styles.Messages} id="chat_messages_field">
			{messages.map(renderRow)}
			<Element name="chat-message-field-bottom" />
		</div>
	);
};
